'use client';

import React from 'react';
import { Drawer } from '../Drawer/Drawer';
import { Icon } from '../../icons';
import './DetailDrawer.css';

export interface DetailDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Callback to close the drawer */
  onOpenChange: (open: boolean) => void;
  /** Title shown in the drawer header */
  title?: string;
  /** Optional subtitle / description */
  subtitle?: string;
  /** Header actions (e.g. edit button, dropdown) */
  headerActions?: React.ReactNode;
  /** Action buttons rendered between header and tabs */
  actions?: React.ReactNode;
  /** Tab navigation rendered between header and body */
  tabs?: React.ReactNode;
  /** Main content of the drawer */
  children: React.ReactNode;
  /** Footer content (e.g. action buttons) */
  footer?: React.ReactNode;
  /** Width of the drawer */
  width?: string | number;
  /** Additional CSS class */
  className?: string;
  /** Number of items selected in bulk mode */
  bulkCount?: number;
  /** Explicit selection mode, including a single checked item. */
  bulkMode?: boolean;
  /** Actions to show in bulk mode */
  bulkChildren?: React.ReactNode;
}

export function DetailDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  headerActions,
  actions,
  tabs,
  children,
  footer,
  width = 480,
  className = '',
  bulkCount = 0,
  bulkMode = bulkCount > 0,
  bulkChildren,
}: DetailDrawerProps) {
  const isBulk = bulkMode;

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
      modal={false}
    >
      <Drawer.Portal>
        <Drawer.Content
          className={`detail-drawer ${className}`.trim()}
          style={{ '--detail-drawer-width': typeof width === 'number' ? `${width}px` : width } as React.CSSProperties}
        >
          <div className="detail-drawer__header">
            <div className="detail-drawer__header-text">
              <h2 className="detail-drawer__title">
                {isBulk ? `${bulkCount} selected` : title}
              </h2>
              {!isBulk && subtitle && (
                <p className="detail-drawer__subtitle">{subtitle}</p>
              )}
            </div>
            <div className="detail-drawer__header-actions">
              {!isBulk && headerActions}
              <Drawer.Close className="detail-drawer__close" aria-label={isBulk ? "Deselect all" : "Close"}>
                <Icon name="X" size={20} />
              </Drawer.Close>
            </div>
          </div>

          {isBulk ? (
            <div className="detail-drawer__bulk">
              {bulkChildren && (
                <div className="detail-drawer__bulk-actions">
                  {bulkChildren}
                </div>
              )}
            </div>
          ) : (
            <>
              {actions && <div className="detail-drawer__actions">{actions}</div>}
              {tabs && <div className="detail-drawer__tabs">{tabs}</div>}
              <div className="detail-drawer__body">{children}</div>
              {footer && <div className="detail-drawer__footer">{footer}</div>}
            </>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
